import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { GlobalVariable } from './../global';
import { BehaviorSubject, Subject } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import { MsalTokenService } from './msal-token.service';
import { MenuService } from './menu.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  rolePermitted: any[] = ['Admin', 'Manager', 'Rep'];
  /** タイムアウトによるログアウト中フラグ（canDeactivate アラートスキップ用） */
  isTimingOut = false;
  private isBrowser: boolean;

  /** ログアウト時に全キャッシュを破棄するためのイベント */
  readonly loggedOut$ = new Subject<void>();

  private userCodeSubject = new BehaviorSubject<string | null>(null);
  userCode$ = this.userCodeSubject.asObservable();
  private userNameSubject = new BehaviorSubject<string | null>(null);
  userName$ = this.userNameSubject.asObservable();
  private companyNameSubject = new BehaviorSubject<string | null>(null);
  companyName$ = this.companyNameSubject.asObservable();
  private userConsentSubject = new BehaviorSubject<string | null>(null);
  userConsent$ = this.userConsentSubject.asObservable();
  
  private loginUrl: string = GlobalVariable.LOGIN_URL;
  private baseUrl: string = GlobalVariable.BASE_URL;
  private lang: string = localStorage.getItem('site_lang') ?? 'en';

  constructor(
    private http: HttpClient, 
    private router: Router,
    private msalToken: MsalTokenService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private menuService: MenuService
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  getEmailContent() {
    const apiKey = this.getToken();
    let apiUrl = this.baseUrl+'/comnotice/getbytermid?termId=2';
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${apiKey}`
    };

    return this.http.get<any>(apiUrl, { headers });
  }

  storeLang(lang: string) {
    if (this.isBrowser) {
      localStorage.setItem('site_lang', lang);
    }
  }

  getLang() {
    return this.isBrowser ? localStorage.getItem('site_lang') : null;
  }

  login(loginObj: any, lang: any) {    
    // let targetUrl = `${this.baseUrl}/User/authenticate`;
    let targetUrl = `${this.loginUrl}/User/authenticate`;
    
    const headers = {
      'Accept-Language': lang === 'en' ? 'en-US' : 'ja-JP'
    };

    return this.http.post<any>(targetUrl, loginObj,{ headers });
    
    // const apiUrl = `${this.baseUrl}/User/authenticate`;
    // return this.http.post<any>(apiUrl, loginObj);    
  }

  /**
   * Authenticate user with profile details in a single API call.
   * This method combines authentication and user profile retrieval to improve login performance.
   * 
   * Benefits:
   * - Reduces API calls from 2 to 1 (30-50% faster login)
   * - Reduces network latency (only 1 round-trip)
   * - Includes CompanyName caching for Approvers
   * - Returns complete user profile with token
   * 
   * Response structure:
   * {
   *   isAuthenticated: boolean,
   *   mustChangePassword: boolean,
   *   token: string,
   *   user: UserDetailsDto (userId, userCode, userName, companyName, email, userRole, permissions, etc.)
   * }
   */
  loginWithProfile(loginObj: any, lang: any) {    
    // let targetUrl = `${this.baseUrl}/User/authenticate-with-profile`;
    let targetUrl = `${this.loginUrl}/User/authenticate-with-profile`;
    
    const headers = {
      'Accept-Language': lang === 'en' ? 'en-US' : 'ja-JP'
    };

    return this.http.post<any>(targetUrl, loginObj, { headers });
  }

  async loginSSO( lang: any) { 
    let loginObj = {};   
    // let targetUrl = `${this.baseUrl}/User/external-sso-authenticate`;
    let targetUrl = `${this.loginUrl}/User/external-sso-authenticate`;

    const token = await this.msalToken.getToken();
    // console.log(token);
        if (!token) return;
      
        const headers = new HttpHeaders({
              Authorization: `Bearer ${token}`,
              auth_type: 'SSO',      // or whatever value you need
              'Accept-Language': lang === 'en' ? 'en-US' : 'ja-JP' // for example 'en', 'jp', 'fr'
            });

    return this.http.post<any>(targetUrl,loginObj,{ headers });
    
    // const apiUrl = `${this.baseUrl}/User/authenticate`;
    // return this.http.post<any>(apiUrl, loginObj);    
  }

  /**
   * Authenticate user via SSO with profile details in a single API call.
   * This method combines SSO authentication and user profile retrieval to improve login performance.
   * 
   * Benefits:
   * - Reduces API calls from 2 to 1 (30-40% faster SSO login)
   * - Reduces network latency (only 1 round-trip)
   * - Includes CompanyName caching (24-hour TTL) for Approvers
   * - Returns complete user profile with token
   * 
   * Response structure:
   * {
   *   isAuthenticated: boolean,
   *   mustChangePassword: boolean,
   *   token: string,
   *   messageId: number,
   *   user: UserDetailsDto (userId, userCode, userName, companyName, email, userRole, permissions, etc.)
   * }
   * 
   * MessageId error codes:
   * - 0: Success
   * - 1: Authorization header missing or not a Bearer token
   * - 2: Malformed or invalid Azure AD token
   * - 3: UPN/email not found in token
   * - 4: User not found in database
   */
  async loginSsoWithProfile(lang: any) { 
    let loginObj = {};   
    // let targetUrl = `${this.baseUrl}/User/external-sso-authenticate-with-profile`;
    let targetUrl = `${this.loginUrl}/User/external-sso-authenticate-with-profile`;

    const token = await this.msalToken.getToken();
    if (!token) return;
      
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      auth_type: 'SSO',
      'Accept-Language': lang === 'en' ? 'en-US' : 'ja-JP'
    });

    return this.http.post<any>(targetUrl, loginObj, { headers });
  }

  logout() {
    var logOutUrl = '';
    const role = this.getRole();
    // role が null の場合（複数タブでのログアウト済み等）は token の有無で SSO ユーザーかどうかを判定
    // token が残っていれば SSO ユーザーと見なして sso-logout へ遷移
    const isSsoUser = role !== null
      ? this.rolePermitted.includes(role)
      : !!this.getToken();
    logOutUrl = isSsoUser ? 'sso-logout' : 'login';
    if (this.isBrowser) {      
      localStorage.removeItem('site_lang');
      localStorage.removeItem('token');
      localStorage.removeItem('userCode');
      localStorage.removeItem('userName');
      localStorage.removeItem('loginID');
      localStorage.removeItem('UserID');  // UserIDも削除 (2026/01/06追加)
      localStorage.removeItem('userRole');
      localStorage.removeItem('userType');
      localStorage.removeItem('adminLevel');
      localStorage.removeItem('readWrite');
      localStorage.removeItem('repId');
      localStorage.removeItem('userEmail');
      localStorage.removeItem('userConsent');
      localStorage.removeItem('userNotice');
      localStorage.removeItem('userPrivecy');
      // localStorage.clear();
      // ensure side menu is closed on logout
      try {
        this.menuService.closeMenu();
      } catch (e) {
        // ignore if MenuService not available for some reason
      }
    }
    // タイムシートキャッシュを全破棄（ログアウトするまで保持していたキャッシュを消去）
    this.loggedOut$.next();
    this.router.navigate([logOutUrl]).then(() => {
      this.isTimingOut = false;
    });
  }

  storeToken(tokenValue: string) {
    if (this.isBrowser) {
      localStorage.setItem('token', tokenValue);
    }
  }

  getToken() {
    return this.isBrowser ? localStorage.getItem('token') : null;
  }
  
  isLoggedIn(): boolean {
    return this.isBrowser ? !!localStorage.getItem('token') : false;
  }

  storeCode(code: string) {
    if (this.isBrowser) {
      localStorage.setItem('userCode', code);
    }
    this.userCodeSubject.next(code);
  }

  getCode() {
    return this.isBrowser ? localStorage.getItem('userCode') : null;
  }

  storeName(loginName: string) {
    if (this.isBrowser) {
      localStorage.setItem('userName', loginName);
    }
    this.userNameSubject.next(loginName);
  }

  getName() {
    return this.isBrowser ? localStorage.getItem('userName') : null;
  }

  storeCompanyName(companyName: string) {
    if (this.isBrowser) {
      localStorage.setItem('companyName', companyName);
    }
    this.userNameSubject.next(companyName);
  }

  getCompanyName() {
    return this.isBrowser ? localStorage.getItem('companyName') : null;
  }

  storeLoginId(loginID: string) {
    if (this.isBrowser) {
      localStorage.setItem('loginID', loginID);
    }
  }

  getLoginId() {
    return this.isBrowser ? localStorage.getItem('loginID') : null;
  }

  // UserID保存・取得機能追加 (2026/01/06)
  // LoginIDは文字列、UserIDは数値（負の数もあり）
  storeUserId(userId: number) {
    if (this.isBrowser) {
      localStorage.setItem('UserID', userId.toString());
    }
  }

  getUserId(): number | null {
    if (!this.isBrowser) return null;
    const userId = localStorage.getItem('UserID');
    if (!userId) return null;
    const parsed = parseInt(userId, 10);
    return isNaN(parsed) ? null : parsed;
  }

  storeRole(role: string) {
    if (this.isBrowser) {
      localStorage.setItem('userRole', role);
    }
  }

  getRole() {
    return this.isBrowser ? localStorage.getItem('userRole') : null;
  }
  
  storeType(userType: string) {
    if (this.isBrowser) {
      localStorage.setItem('userType', userType);
    }
  }

  getType() {
    return this.isBrowser ? localStorage.getItem('userType') : null;
  }

  storeAdminLevel(adminLevel: number) {
    if (this.isBrowser) {
      localStorage.setItem('adminLevel', adminLevel.toString());
    }
  }

  getAdminLevel(): number | null {
    if (!this.isBrowser) return null;
    const level = localStorage.getItem('adminLevel');
    return level ? parseInt(level, 10) : null;
  }

  storeReadWrite(readWrite: boolean) {
    if (this.isBrowser) {
      localStorage.setItem('readWrite', readWrite.toString());
    }
  }

  getReadWrite(): boolean {
    if (!this.isBrowser) return false;
    const readWrite = localStorage.getItem('readWrite');
    return readWrite === 'true';
  }

  storeRepId(repId: string) {
    if (this.isBrowser) {
      localStorage.setItem('repId', repId ?? '');
    }
  }

  getRepId(): string {
    return this.isBrowser ? (localStorage.getItem('repId') ?? '') : '';
  }
  
  storeEmail(email: string) {
    if (this.isBrowser) {
      localStorage.setItem('userEmail', email);
    }
  }

  getEmail() {
    return this.isBrowser ? localStorage.getItem('userEmail') : null;
  }
  
  storeConsent(consent: string) {
    if (this.isBrowser) {
      localStorage.setItem('userConsent', consent);
    }
    this.userConsentSubject.next(consent);
  }

  getConsent() {
    return this.isBrowser ? localStorage.getItem('userConsent') : null;
  }

  storeNotice(notice: string) {
    if (this.isBrowser) {
      localStorage.setItem('userNotice', notice);
    }
  }

  getNotice() {
    return this.isBrowser ? localStorage.getItem('userNotice') : null;
  }
  
  storePrivecy(privecy: string) {
    if (this.isBrowser) {
      localStorage.setItem('userPrivecy', privecy);
    }
  }

  getPrivecy() {
    return this.isBrowser ? localStorage.getItem('userPrivecy') : null;
  }

  storePasswordExpiryDate(date: string | null) {
    if (this.isBrowser) {
      if (date) {
        localStorage.setItem('passwordExpiryDate', date);
      } else {
        localStorage.removeItem('passwordExpiryDate');
      }
    }
  }

  getPasswordExpiryDate() {
    return this.isBrowser ? localStorage.getItem('passwordExpiryDate') : null;
  }

  refreashToken() {
    const apiKey = this.getToken();
    let apiUrl = this.baseUrl+'/user/refresh-token';
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${apiKey}`,
      ssoToken: "",
      supportedLanguage:  localStorage.getItem('site_lang') === 'en' ? '1' : '0' // for example 'en', 'jp'
    };

    return this.http.get<any>(apiUrl, { headers });
  }
}
